import { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserCircle2, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import { Switch } from "@/components/ui/switch";
import { api, formatApiError } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { inputCls } from "@/components/hub/Common";
import { CopyProfileLink } from "@/components/FollowBox";

const LINKS = [["twitch", "Twitch"], ["kick", "Kick"], ["youtube", "YouTube"], ["tiktok", "TikTok"]];

export const ProfileEditor = () => {
  const { t } = useI18n();
  const [p, setP] = useState(null);
  const [saved, setSaved] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/me/profile").then((r) => { setP(r.data); setSaved(r.data); }).catch(() => {}); }, []);
  if (!p) return null;
  const set = (k) => (e) => setP({ ...p, [k]: e.target.value });
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.put("/me/profile", p);
      const next = { ...p, handle: data.handle, links: data.links };
      setP(next);
      setSaved(next);
      toast.success(t("pf.saved"));
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={save} className="cx-card rounded-2xl p-6 space-y-4" data-testid="profile-editor">
      <div className="flex items-start gap-4">
        <UserCircle2 className="w-5 h-5 text-[#3ad7ff] mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="font-display text-white">{t("pf.edit_title")}</div>
          <p className="text-sm text-slate-400 mt-1">{t("pf.edit_desc")}</p>
        </div>
        <Switch checked={p.enabled} onCheckedChange={(v) => setP({ ...p, enabled: v })} data-testid="profile-enabled-switch" />
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <label className="space-y-1"><span className="text-xs text-slate-400">{t("pf.handle")}</span>
          <div className="flex items-center rounded-lg bg-[#070912] border border-white/10 focus-within:border-[#2f7bff]/60">
            <span className="pl-3 text-xs text-slate-500 font-mono">/u/</span>
            <input value={p.handle} onChange={(e) => setP({ ...p, handle: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, "") })} maxLength={24}
              className="flex-1 min-w-0 bg-transparent px-2 py-3 text-sm text-white outline-none" placeholder="cptmaoz" data-testid="profile-handle-input" />
          </div>
        </label>
        <label className="space-y-1"><span className="text-xs text-slate-400">{t("pf.display")}</span>
          <input value={p.display_name} onChange={set("display_name")} maxLength={40} className={inputCls} data-testid="profile-display-input" /></label>
      </div>
      <label className="block space-y-1"><span className="text-xs text-slate-400">{t("pf.bio")}</span>
        <textarea value={p.bio} onChange={set("bio")} maxLength={200} rows={2} className={inputCls} data-testid="profile-bio-input" /></label>
      <div className="grid sm:grid-cols-2 gap-3">
        {LINKS.map(([k, label]) => (
          <label key={k} className="space-y-1"><span className="text-xs text-slate-400">{label}</span>
            <input value={p.links[k] || ""} onChange={(e) => setP({ ...p, links: { ...p.links, [k]: e.target.value } })} maxLength={120}
              placeholder={t("pf.link_ph")} className={inputCls} data-testid={`profile-link-input-${k}`} /></label>
        ))}
      </div>
      <p className="text-[11px] text-slate-500">{t("pf.consent")}</p>
      <div className="flex flex-wrap items-center gap-3">
        <button disabled={busy} className="cx-btn rounded-full px-6 py-2.5 text-sm font-semibold" data-testid="profile-save-btn">{t("pf.save")}</button>
        {saved?.enabled && saved?.handle && (
          <Link to={`/u/${saved.handle}`} target="_blank" className="text-sm text-[#3ad7ff] hover:text-white flex items-center gap-1" data-testid="profile-view-link">
            <ExternalLink className="w-4 h-4" />{t("pf.view")}
          </Link>
        )}
        {saved?.enabled && saved?.handle && <CopyProfileLink handle={saved.handle} testid="profile-editor-copy-link" />}
      </div>
    </form>
  );
};
